/**
 * An amount as the country it is billed in writes it, matching the backend
 * (api/services/local_pricing.py) so the page and Stripe's page agree.
 */
const SYMBOLS = { usd: 'US$', eur: '€', mxn: 'MX$', brl: 'R$', cop: 'COP$', clp: 'CLP$', pen: 'S/', try: '₺' };
// Colombia, Chile and Turkey write 34.900, not 34,900
const DOT_THOUSANDS = new Set(['cop', 'clp', 'try']);

export const currencySymbol = (currency) => SYMBOLS[currency] || `${String(currency || '').toUpperCase()} `;

/** A whole amount with thousands commas and Western digits: 1999 → "1,999". */
export const wholeNumber = (amount) => String(Math.round(Number(amount) || 0)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Currencies nobody writes with decimals (the backend's WHOLE_UNITS and ZERO_DECIMAL)
const WHOLE_UNITS = new Set(['cop', 'clp', 'mxn', 'pen', 'try']);

export function formatMoney(currency, amount) {
  const value = Number(amount) || 0;
  const thousands = (digits) => digits.replace(/\B(?=(\d{3})+(?!\d))/g, DOT_THOUSANDS.has(currency) ? '.' : ',');
  if (Number.isInteger(value) || WHOLE_UNITS.has(currency)) {
    return `${currencySymbol(currency)}${thousands(String(Math.round(value)))}`;
  }
  // €6.67, US$8.33: two decimals, the thousands still separated (a yearly total a month)
  const [whole, cents] = value.toFixed(2).split('.');
  return `${currencySymbol(currency)}${thousands(whole)}.${cents}`;
}

// Stripe bills these in whole units, the rest in cents (the backend's ZERO_DECIMAL)
const ZERO_DECIMAL = new Set(['clp']);

/**
 * What the half-price first month charges: half the amount, when that is a whole
 * number of the currency's smallest unit (MX$159 → 79.50, CLP$8.490 → 4.245), else
 * null. Egypt's US$5.75 halves to 287.5 cents, which Stripe rounds its own way, so
 * the page does not name a number it cannot be sure of.
 */
export function halfAmount(currency, amount) {
  const minor = ZERO_DECIMAL.has(currency) ? 1 : 100;
  const units = Math.round((Number(amount) || 0) * minor);
  if (!units || units % 2) return null;
  return units / 2 / minor;
}

/** An amount to the cent: MX$79.50 where formatMoney would round to MX$80. */
export function formatExact(currency, amount) {
  const value = Number(amount) || 0;
  if (Number.isInteger(value)) return formatMoney(currency, value);
  const [whole, cents] = value.toFixed(2).split('.');
  const thousands = whole.replace(/\B(?=(\d{3})+(?!\d))/g, DOT_THOUSANDS.has(currency) ? '.' : ',');
  return `${currencySymbol(currency)}${thousands}.${cents}`;
}
